import { createFileRoute } from '@tanstack/react-router'
import {
  Landmark,
  HeartHandshake,
  Wallet,
  Award,
  Phone,
  Users,
} from 'lucide-react'
import { motion } from 'framer-motion'
import {
  Container,
  PageHero,
  SectionHeading,
  FeatureCard,
  CtaBand,
  fadeUp,
} from '@/components/umbrella/ui'

export const Route = createFileRoute('/solutions/defense-credit-unions')({
  component: DefenseCreditUnionsPage,
})

/** What the program does for the credit union. */
const BENEFITS = [
  {
    Icon: Wallet,
    title: 'Fewer losses and disputes',
    body: 'Every transfer you help a member stop is a dispute, chargeback, or recovery case you never have to work. Prevention costs far less than reimbursement.',
  },
  {
    Icon: HeartHandshake,
    title: 'Deeper member trust',
    body: 'Members remember who helped them avoid a costly mistake. Practical protection strengthens the relationship you have already earned.',
  },
  {
    Icon: Award,
    title: 'A member benefit that stands out',
    body: "Offer something most institutions don't: ongoing scam education built for military life, delivered in your name.",
  },
  {
    Icon: Users,
    title: 'Protects the whole family',
    body: 'Deployments and moves often leave a spouse or parent managing the money. Equip the whole household, not just the account holder.',
  },
]

/** Scams that most distinctly affect the military community. */
const SCAMS = [
  {
    title: 'Government and benefits imposters',
    body: 'Callers impersonate the VA, DFAS, or the IRS, claiming benefits are suspended or details must be reverified. The web of legitimate federal payers makes a fake one easy to slip in.',
  },
  {
    title: 'VA benefit redirection and pension poaching',
    body: 'A "benefits consultant" charges illegal upfront fees or redirects pension and disability payments. Charging for VA claims help is restricted by law, so any upfront fee is a red flag.',
  },
  {
    title: 'Deployment romance scams',
    body: 'Scammers pose as deployed service members, then request money for leave paperwork, travel, or medical care. The military charges members for none of these.',
  },
  {
    title: 'PCS move and rental scams',
    body: 'Families searching remotely on a fixed timeline meet fake rental listings and fraudulent movers. The deadline pressure of a PCS is exactly what these scams exploit.',
  },
  {
    title: 'Predatory lending around payday',
    body: 'Fast-cash and allotment-based loans are timed to military pay dates and structured to evade Military Lending Act protections, which cap the Military APR at 36%.',
  },
  {
    title: 'Affinity and veteran-to-veteran scams',
    body: 'A pitch leans on shared service or veteran status to lower the guard before a fraudulent investment or donation. Fake veteran charities spike around Memorial Day and Veterans Day.',
  },
]

/** Defensive behaviors that actually work. */
const BEHAVIORS = [
  {
    title: 'Verify on a number you found yourself',
    body: 'If the VA, DFAS, the IRS, the credit union, or "command" calls about money or benefits, hang up and call back using the number on your card or the official site. Scammers resist a call-back.',
  },
  {
    title: 'Treat urgency plus a payment method as the alarm',
    body: 'Pressure to act now, combined with a request for gift cards, wire transfers, crypto, or payment apps, is the signature of a scam. When both appear together, stop and verify.',
  },
  {
    title: 'Confirm housing, movers, and benefits officially',
    body: 'Verify PCS rentals and movers through the installation housing office or Military OneSource. Use VA.gov or an accredited representative for benefits, and never pay upfront for claims help.',
  },
  {
    title: 'Slow down money to "deployed" or "command" requests',
    body: 'No deployed member needs a stranger to wire money, and no real superior asks for gift cards. For loans, confirm the offer respects the 36% Military APR cap before signing.',
  },
]

function DefenseCreditUnionsPage() {
  return (
    <div className="bg-white">
      <PageHero
        label="For Defense Credit Unions"
        title="Fraud protection education built for"
        highlight="the military community."
        blurb="Active duty members, military families, and veterans face the same scams as everyone else, plus a distinct set that exploit the rhythms of military life: steady pay, deployments, frequent moves, and federal benefits. Give your members the education to see them coming, delivered in your name."
        primary={{ label: 'Talk to our team', href: '#contact' }}
        secondary={{ label: 'See the platform', href: '/platform' }}
      />

      {/* What the credit union gains */}
      <Container className="py-20">
        <SectionHeading
          label="Why it pays"
          title="Protection for your members, value for your credit union"
          blurb="Stopping a scam before the transfer helps your member and your credit union at the same time."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map(b => (
            <FeatureCard key={b.title} {...b} />
          ))}
        </div>
      </Container>

      {/* Scams targeting the military community */}
      <section className="bg-slate-50 py-20">
        <Container>
          <SectionHeading
            label="Threats your members face"
            title="Scams that target the military community"
            blurb="These are the patterns that most distinctly affect service members, their families, and veterans. Education covers all of them, in plain language."
          />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SCAMS.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: (i % 3) * 0.06 }}
                className="rounded-2xl border border-slate-200/80 bg-white p-6"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <Phone className="h-4 w-4" />
                </span>
                <h3 className="mt-4 text-[15px] font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-xs leading-relaxed text-slate-400">
            Sources: FTC Consumer Sentinel Network (military consumer fraud
            reporting) and the CFPB Office of Servicemember Affairs. The 36%
            Military APR cap is set by the Military Lending Act.
          </p>
        </Container>
      </section>

      {/* What members learn to do */}
      <Container className="py-20">
        <SectionHeading
          label="What members learn"
          title="A few behaviors, practiced until reflexive"
          blurb="Skip the long checklists. These are the moves that stop most of these scams under real pressure."
        />
        <div className="mx-auto mt-12 grid max-w-4xl gap-4 sm:grid-cols-2">
          {BEHAVIORS.map((b, i) => (
            <motion.div
              key={b.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: (i % 2) * 0.06 }}
              className="rounded-2xl border border-slate-200/80 bg-white p-6"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                {i + 1}
              </span>
              <h3 className="mt-4 text-[15px] font-semibold text-slate-900">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.body}</p>
            </motion.div>
          ))}
        </div>
      </Container>

      {/* Deployment note */}
      <section className="bg-slate-50 py-20">
        <Container>
          <motion.div
            {...fadeUp}
            className="flex flex-col items-start gap-5 rounded-3xl border border-slate-200/80 bg-white p-8 sm:p-12"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white">
              <Landmark className="h-6 w-6" />
            </span>
            <SectionHeading
              label="Deployed in your name"
              title="Member-branded, ready for your channels"
              align="left"
            />
            <p className="max-w-2xl text-lg leading-relaxed text-slate-600">
              We deliver the platform in your credit union’s branding, ready to
              link from online banking, your app, branch screens, and member
              emails. You get a turnkey member benefit without building or
              maintaining content yourself.
            </p>
          </motion.div>
        </Container>
      </section>

      <CtaBand
        title="Bring fraud prevention to your members."
        blurb="Let’s talk about a member-branded ScamDefense deployment for your credit union."
      />
    </div>
  )
}
